import { BaseConnection, parseFormat, } from "./BaseConnection.js";
import { sourceInfo } from "../sourceInfo.js";
import { isValidSocketEvent, } from "./events.js";
import { constructOverrides } from "./overrides.js";
import { SessionConnectionError } from "./errors.js";
const MAIN_PROTOCOL = "convai";
const WSS_API_ORIGIN = "wss://api.elevenlabs.io";
const WSS_API_PATHNAME = "/v1/convai/conversation?agent_id=";
export class WebSocketConnection extends BaseConnection {
    socket;
    conversationId;
    inputFormat;
    outputFormat;
    outputListeners = new Set();
    constructor(socket, conversationId, inputFormat, outputFormat) {
        super();
        this.socket = socket;
        this.conversationId = conversationId;
        this.inputFormat = inputFormat;
        this.outputFormat = outputFormat;
        this.socket.addEventListener("error", event => {
            // In case the error event is followed by a close event, we want the
            // latter to be the one that disconnects the session as it contains more
            // useful information.
            setTimeout(() => this.disconnect({
                reason: "error",
                message: "The connection was closed due to a socket error.",
                context: event,
            }), 0);
        });
        this.socket.addEventListener("close", event => {
            this.disconnect(event.code === 1000
                ? {
                    reason: "agent",
                    context: event,
                    closeCode: event.code,
                    closeReason: event.reason || undefined,
                }
                : {
                    reason: "error",
                    message: event.reason || "The connection was closed by the server.",
                    context: event,
                    closeCode: event.code,
                    closeReason: event.reason || undefined,
                });
        });
        this.socket.addEventListener("message", event => {
            try {
                const parsedEvent = JSON.parse(event.data);
                if (!isValidSocketEvent(parsedEvent)) {
                    this.debug({
                        type: "invalid_event",
                        message: "Received invalid socket event",
                        data: event.data,
                    });
                    return;
                }
                this.handleMessage(parsedEvent);
            }
            catch (error) {
                this.debug({
                    type: "parsing_error",
                    message: "Failed to parse socket message",
                    error: error instanceof Error ? error.message : String(error),
                    data: event.data,
                });
            }
        });
    }
    static async create(config) {
        let socket = null;
        try {
            const origin = config.origin ?? WSS_API_ORIGIN;
            let url;
            const { name: source, version } = sourceInfo;
            if (config.signedUrl) {
                const separator = config.signedUrl.includes("?") ? "&" : "?";
                url = `${config.signedUrl}${separator}source=${source}&version=${version}`;
            }
            else {
                url = `${origin}${WSS_API_PATHNAME}${config.agentId}&source=${source}&version=${version}`;
            }
            if (config.environment) {
                url += `&environment=${encodeURIComponent(config.environment)}`;
            }
            const protocols = [MAIN_PROTOCOL];
            if (config.authorization) {
                protocols.push(`bearer.${config.authorization}`);
            }
            socket = new WebSocket(url, protocols);
            const conversationConfig = await new Promise((resolve, reject) => {
                socket.addEventListener("open", () => {
                    const overridesEvent = constructOverrides(config);
                    socket?.send(JSON.stringify(overridesEvent));
                }, { once: true });
                socket.addEventListener("error", event => {
                    // In case the error event is followed by a close event, we want the
                    // latter to be the one that rejects the promise as it contains more
                    // useful information.
                    setTimeout(() => reject(new SessionConnectionError("The connection was closed due to a socket error.")), 0);
                });
                socket.addEventListener("close", (event) => {
                    const message = event.reason ||
                        (event.code === 1000
                            ? "Connection closed normally before session could be established."
                            : "Connection closed unexpectedly before session could be established.");
                    reject(new SessionConnectionError(message, {
                        closeCode: event.code,
                        closeReason: event.reason || undefined,
                    }));
                });
                socket.addEventListener("message", (event) => {
                    const message = JSON.parse(event.data);
                    if (!isValidSocketEvent(message)) {
                        return;
                    }
                    if (message.type === "conversation_initiation_metadata") {
                        resolve(message.conversation_initiation_metadata_event);
                    }
                    else {
                        console.warn("First received message is not conversation metadata.");
                    }
                }, { once: true });
            });
            const { conversation_id, agent_output_audio_format, user_input_audio_format, } = conversationConfig;
            const inputFormat = parseFormat(user_input_audio_format ?? "pcm_16000");
            const outputFormat = parseFormat(agent_output_audio_format);
            return new WebSocketConnection(socket, conversation_id, inputFormat, outputFormat);
        }
        catch (error) {
            socket?.close();
            throw error;
        }
    }
    close() {
        this.socket.close(1000, "User ended conversation");
    }
    sendMessage(message) {
        this.socket.send(JSON.stringify(message));
    }
    addListener(listener) {
        this.outputListeners.add(listener);
    }
    removeListener(listener) {
        this.outputListeners.delete(listener);
    }
    handleMessage(parsedEvent) {
        super.handleMessage(parsedEvent);
        // Emit audio events to output listeners
        if (parsedEvent.type === "audio" && parsedEvent.audio_event.audio_base_64) {
            const audioEvent = {
                audio_base_64: parsedEvent.audio_event.audio_base_64,
            };
            this.outputListeners.forEach(listener => listener(audioEvent));
        }
    }
}
//# sourceMappingURL=WebSocketConnection.js.map